import moment from "moment-timezone";
import {
  getClientTimezone,
  parseUtcInstant,
  parseToLocalMs,
  formatCalendarDateForApi,
} from "./dateTimeUtils";

export { getClientTimezone, parseUtcInstant, parseToLocalMs };

export const LIDAR_DATE_PRESETS = {
  today: "today",
  yesterday: "yesterday",
  last7days: "last7days",
  last30days: "last30days",
  thisMonth: "thismonth",
  custom: "custom",
};

export const LIDAR_DATE_PRESET_OPTIONS = [
  { value: LIDAR_DATE_PRESETS.today, label: "Today" },
  { value: LIDAR_DATE_PRESETS.yesterday, label: "Yesterday" },
  { value: LIDAR_DATE_PRESETS.last7days, label: "Last 7 Days" },
  { value: LIDAR_DATE_PRESETS.last30days, label: "Last 30 Days" },
  { value: LIDAR_DATE_PRESETS.thisMonth, label: "This Month" },
  { value: LIDAR_DATE_PRESETS.custom, label: "Custom Date Range" },
];

export function getPresetLabel(preset) {
  return (
    LIDAR_DATE_PRESET_OPTIONS.find((o) => o.value === preset)?.label ?? "Period"
  );
}

export const DEFAULT_CUSTOM_FROM_TIME = "00:00";
export const DEFAULT_CUSTOM_TO_TIME = "23:59";

export function createEmptyDateFilter() {
  return {
    preset: "",
    fromDate: null,
    toDate: null,
    fromTime: DEFAULT_CUSTOM_FROM_TIME,
    toTime: DEFAULT_CUSTOM_TO_TIME,
  };
}

export function createDefaultDateFilter() {
  return {
    preset: LIDAR_DATE_PRESETS.today,
    fromDate: null,
    toDate: null,
    fromTime: DEFAULT_CUSTOM_FROM_TIME,
    toTime: DEFAULT_CUSTOM_TO_TIME,
  };
}

/** Today in client TZ — used when opening custom range. */
export function getDefaultCustomDateFilter() {
  const clientTz = getClientTimezone();
  const today = moment.tz(clientTz).startOf("day").toDate();
  return {
    fromDate: today,
    toDate: today,
    fromTime: DEFAULT_CUSTOM_FROM_TIME,
    toTime: DEFAULT_CUSTOM_TO_TIME,
  };
}

function combineDateTimeInClientTz(date, timeStr, clientTz) {
  const [hour, minute] = (timeStr || DEFAULT_CUSTOM_FROM_TIME)
    .split(":")
    .map((part) => Number.parseInt(part, 10));
  return moment.tz(
    {
      year: date.getFullYear(),
      month: date.getMonth(),
      date: date.getDate(),
      hour: Number.isFinite(hour) ? hour : 0,
      minute: Number.isFinite(minute) ? minute : 0,
      second: 0,
      millisecond: 0,
    },
    clientTz,
  );
}

/**
 * Resolve range to UTC millisecond bounds [start, endExclusive).
 * Presets use the browser timezone; bounds are UTC instants for API filtering.
 */
export function resolveDateFilterRange(
  preset,
  customFrom,
  customTo,
  fromTime = DEFAULT_CUSTOM_FROM_TIME,
  toTime = DEFAULT_CUSTOM_TO_TIME,
) {
  if (!preset) return null;

  const clientTz = getClientTimezone();
  const now = moment.tz(clientTz);

  if (preset === LIDAR_DATE_PRESETS.custom) {
    if (!customFrom || !customTo) return null;

    const fromMoment = combineDateTimeInClientTz(
      customFrom,
      fromTime,
      clientTz,
    );
    const toMoment = combineDateTimeInClientTz(customTo, toTime, clientTz);
    if (!fromMoment.isValid() || !toMoment.isValid()) return null;
    if (fromMoment.isAfter(toMoment)) return null;

    return {
      preset,
      startMs: fromMoment.valueOf(),
      endMsExclusive: toMoment.clone().add(1, "minute").valueOf(),
      periodLabel: `${fromMoment.format("DD MMM YYYY, hh:mm A")} – ${toMoment.format("DD MMM YYYY, hh:mm A")}`,
    };
  }

  let from;
  let toExclusive;

  switch (preset) {
    case LIDAR_DATE_PRESETS.today:
      from = now.clone().startOf("day");
      toExclusive = from.clone().add(1, "day");
      break;
    case LIDAR_DATE_PRESETS.yesterday:
      from = now.clone().subtract(1, "day").startOf("day");
      toExclusive = now.clone().startOf("day");
      break;
    case LIDAR_DATE_PRESETS.last7days:
      from = now.clone().subtract(6, "day").startOf("day");
      toExclusive = now.clone().startOf("day").add(1, "day");
      break;
    case LIDAR_DATE_PRESETS.last30days:
      from = now.clone().subtract(29, "day").startOf("day");
      toExclusive = now.clone().startOf("day").add(1, "day");
      break;
    case LIDAR_DATE_PRESETS.thisMonth:
      from = now.clone().startOf("month");
      toExclusive = from.clone().add(1, "month");
      break;
    default:
      return null;
  }

  return {
    preset,
    startMs: from.valueOf(),
    endMsExclusive: toExclusive.valueOf(),
    periodLabel: getPresetLabel(preset),
  };
}

function parseFilterInstantMs(value) {
  return parseToLocalMs(value);
}

/** Lidar date filter: payload `timestamp` only (API `timestamp`). */
export function getLidarDateFilterMs(row) {
  if (!row) return null;
  return parseFilterInstantMs(row.timestamp);
}

/** Sensor date filter: `receivedAt` only. */
export function getSensorDateFilterMs(row) {
  if (!row) return null;
  return parseFilterInstantMs(row.receivedAt);
}

export function rowMatchesLidarDateFilter(row, range) {
  if (!range) return true;
  const ts = getLidarDateFilterMs(row);
  if (ts == null) return false;
  return ts >= range.startMs && ts < range.endMsExclusive;
}

export function rowMatchesSensorDateFilter(row, range) {
  if (!range) return true;
  const ts = getSensorDateFilterMs(row);
  if (ts == null) return false;
  return ts >= range.startMs && ts < range.endMsExclusive;
}

export function isCustomDateRangeComplete(
  fromDate,
  toDate,
  fromTime = DEFAULT_CUSTOM_FROM_TIME,
  toTime = DEFAULT_CUSTOM_TO_TIME,
) {
  if (!fromDate || !toDate) return false;
  const clientTz = getClientTimezone();
  const from = combineDateTimeInClientTz(fromDate, fromTime, clientTz);
  const to = combineDateTimeInClientTz(toDate, toTime, clientTz);
  return from.isValid() && to.isValid() && !from.isAfter(to);
}

/** Field-level errors — no noise when both dates are still empty. */
export function getCustomDateRangeErrors(
  fromDate,
  toDate,
  fromTime = DEFAULT_CUSTOM_FROM_TIME,
  toTime = DEFAULT_CUSTOM_TO_TIME,
) {
  const errors = { from: "", to: "", general: "" };

  if (!fromDate && !toDate) return errors;

  if (!fromDate) {
    errors.from = "Select a start date";
  }
  if (!toDate) {
    errors.to = "Select an end date";
  }

  if (fromDate && toDate) {
    const clientTz = getClientTimezone();
    const from = combineDateTimeInClientTz(fromDate, fromTime, clientTz);
    const to = combineDateTimeInClientTz(toDate, toTime, clientTz);
    if (from.isAfter(to)) {
      errors.general = "Start cannot be after end.";
    }
  }

  return errors;
}

export function validateCustomDateRange(fromDate, toDate, fromTime, toTime) {
  const { from, to, general } = getCustomDateRangeErrors(
    fromDate,
    toDate,
    fromTime,
    toTime,
  );
  if (general) return general;
  if (from?.trim()) return from.trim();
  if (to?.trim()) return to.trim();
  return "";
}

export function isDateFilterValid(dateFilter) {
  if (!dateFilter?.preset) return true;
  if (dateFilter.preset !== LIDAR_DATE_PRESETS.custom) return true;
  return isCustomDateRangeComplete(
    dateFilter.fromDate,
    dateFilter.toDate,
    dateFilter.fromTime,
    dateFilter.toTime,
  );
}

export function buildDateFilterApiParams(dateFilter) {
  if (!dateFilter?.preset || !isDateFilterValid(dateFilter)) return {};

  const resolved = resolveDateFilterRange(
    dateFilter.preset,
    dateFilter.fromDate,
    dateFilter.toDate,
    dateFilter.fromTime,
    dateFilter.toTime,
  );

  const params = {
    datePreset: dateFilter.preset,
    clientTimezone: getClientTimezone(),
  };

  // Client-resolved UTC bounds — do not rely on server clock for "today".
  if (resolved) {
    params.from = new Date(resolved.startMs).toISOString();
    params.to = new Date(resolved.endMsExclusive).toISOString();
  }

  if (dateFilter.preset === LIDAR_DATE_PRESETS.custom) {
    if (dateFilter.fromDate) {
      params.fromDate = formatCalendarDateForApi(dateFilter.fromDate);
    }
    if (dateFilter.toDate) {
      params.toDate = formatCalendarDateForApi(dateFilter.toDate);
    }
    params.fromTime = dateFilter.fromTime || DEFAULT_CUSTOM_FROM_TIME;
    params.toTime = dateFilter.toTime || DEFAULT_CUSTOM_TO_TIME;
  }

  return params;
}

export function buildChartFilterApiParams(dateFilter) {
  return buildDateFilterApiParams(dateFilter);
}

export function chartPeriodToDateRange(period) {
  if (!period?.fromUtc || !period?.toUtcExclusive) return null;
  const startMs = Date.parse(period.fromUtc);
  const endMsExclusive = Date.parse(period.toUtcExclusive);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMsExclusive)) {
    return null;
  }
  const now = Date.now();
  return {
    startMs,
    endMsExclusive: Math.min(endMsExclusive, now),
  };
}

export function formatDateForApi(date) {
  return formatCalendarDateForApi(date);
}
